import {useOktaAuth} from '@okta/okta-react';
import {OnAuthRequiredFunction} from '@okta/okta-react/bundles/types/OktaContext';
import {Alert, Pane} from 'evergreen-ui';
import React, {useEffect, useRef} from 'react';
import {Route, RouteProps, useRouteMatch} from 'react-router';
import {useCurrentUser} from './CurrentUser';


type PortalSecureRouteProps = RouteProps & {
    allowedRoles?: readonly string[];
    onAuthRequired?: OnAuthRequiredFunction;
};

/**
 * Use this component instead of Okta's SecureRoute
 * @param props
 * @constructor
 */
export const PortalSecureRoute = ({allowedRoles, onAuthRequired, ...routerProps}: PortalSecureRouteProps) => {
    const currentUser = useCurrentUser();

    const {oktaAuth, authState, _onAuthRequired} = useOktaAuth();
    const match = useRouteMatch(routerProps);
    const pendingLogin = useRef(false);

    useEffect(() => {
        const handleLogin = async () => {
            if (pendingLogin.current) {
                return;
            }

            pendingLogin.current = true;

            // for detail: https://github.com/okta/okta-auth-js/pull/678/files
            oktaAuth.setOriginalUri(window.location.href);
            const onAuthRequiredFn = onAuthRequired || _onAuthRequired;
            if (onAuthRequiredFn) {
                await onAuthRequiredFn(oktaAuth);
            } else {
                await oktaAuth.signInWithRedirect();
            }
        };

        // Only process logic if the route matches
        if (!match) {
            return;
        }

        if (authState?.isAuthenticated) {
            pendingLogin.current = false;
            return;
        }

        // Start login if app has decided it is not logged in and there is no pending signin
        if (!authState?.isAuthenticated) {
            handleLogin();
        }

    }, [
        authState?.isAuthenticated,
        oktaAuth,
        match,
        onAuthRequired,
        _onAuthRequired
    ]);

    if (!authState?.isAuthenticated || !currentUser) {
        return null;
    }

    if (!(allowedRoles ?? [currentUser.role]).includes(currentUser.role)) {
        return (
            <Pane height='100vh'>
                <Alert intent='danger' title='Insufficient Permission'>
                    You do not have permission to view this page.
                </Alert>
            </Pane>
        );
    }

    return (
        <Route {...routerProps}/>
    );
};
